import React, { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { useToast } from "../components/ui/Toast";
import { Button, Input, Card, Badge } from "../components/ui";
import Modal from "../components/ui/Modal";
import { getToken } from "../services/tokenStore";
import PasswordChangeModal from "../components/PasswordChangeModal";
import IntegrazioneApiProfilo from "../components/IntegrazioneApiProfilo";

function ProfilePage() {
  const { user, apiRequest, isDevMode } = useAuth();
  const { showToast } = useToast();

  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(false);
  const [formData, setFormData] = useState({
    displayName: "",
  });
  const [stats, setStats] = useState({
    interventi: 0,
    interventiDaValidare: 0,
    scadenzeImminenti: 0,
  });

  // Privacy section state
  const [privacySummary, setPrivacySummary] = useState(null);
  const [privacySummaryLoading, setPrivacySummaryLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteReason, setDeleteReason] = useState("");
  const [deleteConfirmEmail, setDeleteConfirmEmail] = useState("");
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);

  useEffect(() => {
    if (user) {
      setFormData({
        displayName: user.displayName || "",
      });
      fetchStats();
    }
  }, [user]);

  const fetchStats = async () => {
    try {
      // Fetch user-specific stats
      const [interventiRes, scadenzeRes] = await Promise.all([
        apiRequest("/interventi/recenti").catch(() => ({ value: [] })),
        apiRequest("/scadenze?stato=in_scadenza").catch(() => ({ value: [] })),
      ]);

      const interventi = interventiRes.value || [];

      setStats({
        interventi: interventi.length,
        interventiDaValidare: interventi.filter((i) => !i.validato).length,
        scadenzeImminenti: (scadenzeRes.value || []).length,
      });
    } catch (error) {
      console.error("Error fetching stats:", error);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSave = async () => {
    if (!formData.displayName.trim()) {
      showToast("Il nome visualizzato è obbligatorio", "warning");
      return;
    }

    setLoading(true);
    try {
      await apiRequest("/auth/me", {
        method: "PUT",
        body: JSON.stringify({
          displayName: formData.displayName,
        }),
      });

      showToast("Profilo aggiornato con successo", "success");
      setEditing(false);
    } catch (error) {
      console.error("Error updating profile:", error);
      showToast(error.message || "Errore nell'aggiornamento", "error");
    } finally {
      setLoading(false);
    }
  };

  const getRuoleBadgeVariant = (ruolo) => {
    const variants = {
      Admin: "danger",
      RSPP: "warning",
      Direzione: "primary",
      "Direttore RSA": "info",
      Amministrativo: "info",
      Fornitore: "success",
      Stakeholder: "info",
    };
    return variants[ruolo] || "default";
  };

  const fetchPrivacySummary = async () => {
    setPrivacySummaryLoading(true);
    try {
      const data = await apiRequest("/privacy/my-data/summary");
      setPrivacySummary(data);
    } catch (error) {
      console.error("Error fetching privacy summary:", error);
      showToast("Errore nel caricamento dei dati privacy", "error");
    } finally {
      setPrivacySummaryLoading(false);
    }
  };

  const handleExportData = async (format) => {
    setExportLoading(format);
    try {
      const token = getToken();
      const response = await fetch(
        `/api/privacy/my-data${format === "csv" ? "?format=csv" : ""}`,
        {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        },
      );
      if (!response.ok) throw new Error("Errore durante l'export");

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `i-miei-dati.${format === "csv" ? "csv" : "json"}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      showToast(`Dati esportati in formato ${format.toUpperCase()}`, "success");
    } catch (error) {
      console.error("Error exporting data:", error);
      showToast("Errore durante l'esportazione dei dati", "error");
    } finally {
      setExportLoading(null);
    }
  };

  const handleDeleteRequest = async () => {
    if (deleteConfirmEmail !== user.email) {
      showToast("L'email di conferma non corrisponde", "warning");
      return;
    }

    setDeleteLoading(true);
    try {
      await apiRequest("/privacy/my-data", {
        method: "DELETE",
        body: JSON.stringify({
          reason: deleteReason,
          confirmEmail: deleteConfirmEmail,
        }),
      });
      setShowDeleteModal(false);
      setDeleteReason("");
      setDeleteConfirmEmail("");
      showToast(
        "Richiesta di cancellazione registrata. Verrai contattato dal DPO entro 30 giorni.",
        "success",
      );
    } catch (error) {
      console.error("Error requesting deletion:", error);
      showToast(
        error.message || "Errore nella richiesta di cancellazione",
        "error",
      );
    } finally {
      setDeleteLoading(false);
    }
  };

  if (!user) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner w-10 h-10"></div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Il mio Profilo</h1>
        <p className="text-gray-600 mt-1">
          Gestisci le informazioni del tuo account
        </p>
      </div>

      {/* User Info Card */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        <Card className="lg:col-span-2">
          <div className="flex items-start justify-between mb-6">
            <div className="flex items-center gap-4">
              {/* Avatar */}
              <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center">
                <span className="text-2xl font-bold text-primary-600">
                  {user.displayName?.charAt(0)?.toUpperCase() ||
                    user.email?.charAt(0)?.toUpperCase() ||
                    "?"}
                </span>
              </div>
              <div>
                <h2 className="text-xl font-semibold text-gray-900">
                  {user.displayName}
                </h2>
                <p className="text-gray-500">{user.email}</p>
                <div className="flex items-center gap-2 mt-2">
                  <Badge variant={getRuoleBadgeVariant(user.ruoloLabel)}>
                    {user.ruoloLabel}
                  </Badge>
                  {user.attivo === false && (
                    <Badge variant="warning">In attesa di attivazione</Badge>
                  )}
                  {isDevMode && user.isTestUser && (
                    <Badge variant="default">Utente Test</Badge>
                  )}
                </div>
              </div>
            </div>
            {!editing && (
              <Button variant="outline" onClick={() => setEditing(true)}>
                Modifica
              </Button>
            )}
          </div>

          {editing ? (
            <div className="space-y-4">
              <Input
                label="Nome Visualizzato"
                name="displayName"
                value={formData.displayName}
                onChange={handleChange}
                placeholder="Il tuo nome completo"
                required
              />

              <div className="flex justify-end gap-3">
                <Button
                  variant="outline"
                  onClick={() => {
                    setEditing(false);
                    setFormData({ displayName: user.displayName || "" });
                  }}
                  disabled={loading}
                >
                  Annulla
                </Button>
                <Button onClick={handleSave} loading={loading}>
                  Salva
                </Button>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-500 mb-1">
                  Email
                </label>
                <p className="text-gray-900">{user.email}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500 mb-1">
                  Ruolo
                </label>
                <p className="text-gray-900">{user.ruoloLabel}</p>
              </div>
              {user.rsa && (
                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-1">
                    RSA Assegnata
                  </label>
                  <p className="text-gray-900">{user.rsa.nome}</p>
                </div>
              )}
              {user.fornitore && (
                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-1">
                    Fornitore
                  </label>
                  <p className="text-gray-900">{user.fornitore.nome}</p>
                </div>
              )}
              {user.ultimoAccesso && (
                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-1">
                    Ultimo Accesso
                  </label>
                  <p className="text-gray-900">
                    {new Date(user.ultimoAccesso).toLocaleString("it-IT")}
                  </p>
                </div>
              )}
            </div>
          )}
        </Card>

        {/* Stats */}
        <div className="space-y-4">
          <Card>
            <div className="text-center">
              <p className="text-3xl font-bold text-primary-600">
                {stats.interventi}
              </p>
              <p className="text-sm text-gray-500">Interventi (30gg)</p>
            </div>
          </Card>
          <Card>
            <div className="text-center">
              <p className="text-3xl font-bold text-yellow-600">
                {stats.interventiDaValidare}
              </p>
              <p className="text-sm text-gray-500">Da Validare</p>
            </div>
          </Card>
          <Card>
            <div className="text-center">
              <p className="text-3xl font-bold text-red-600">
                {stats.scadenzeImminenti}
              </p>
              <p className="text-sm text-gray-500">Scadenze Imminenti</p>
            </div>
          </Card>
        </div>
      </div>

      {/* Security Info */}
      <Card title="Sicurezza">
        <div className="space-y-4">
          <div className="flex items-center justify-between py-3 border-b">
            <div>
              <p className="font-medium text-gray-900">Autenticazione</p>
              <p className="text-sm text-gray-500">
                {isDevMode
                  ? "Modalità sviluppo (JWT locale)"
                  : "Azure CIAM + Email OTP"}
              </p>
            </div>
            <Badge variant={isDevMode ? "warning" : "success"}>
              {isDevMode ? "Dev Mode" : "Sicuro"}
            </Badge>
          </div>

          <div className="flex items-center justify-between py-3 border-b">
            <div>
              <p className="font-medium text-gray-900">Password</p>
              {user.dataUltimoCambioPassword ? (
                <p className="text-sm text-gray-500">
                  Ultimo cambio:{" "}
                  {new Date(user.dataUltimoCambioPassword).toLocaleDateString(
                    "it-IT",
                  )}
                  {user.giorniAllaScadenzaPassword !== null && (
                    <span
                      className={
                        user.giorniAllaScadenzaPassword <= 0
                          ? "text-red-600 font-medium ml-2"
                          : user.giorniAllaScadenzaPassword <= 30
                            ? "text-amber-600 font-medium ml-2"
                            : "text-gray-500 ml-2"
                      }
                    >
                      {user.giorniAllaScadenzaPassword <= 0
                        ? "(scaduta)"
                        : `(scade tra ${user.giorniAllaScadenzaPassword} giorni)`}
                    </span>
                  )}
                </p>
              ) : (
                <p className="text-sm text-amber-600">
                  Password non ancora impostata
                </p>
              )}
            </div>
            {!isDevMode && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowPasswordModal(true)}
              >
                Cambia Password
              </Button>
            )}
          </div>

          {user.passwordScaduta && !isDevMode && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-800 font-medium">
                Password scaduta
              </p>
              <p className="text-sm text-red-700 mt-1">
                Per motivi di sicurezza, è necessario cambiare la password ogni
                6 mesi. Clicca "Cambia Password" per aggiornarla.
              </p>
            </div>
          )}
        </div>
      </Card>

      {/* Password change modal */}
      {showPasswordModal && (
        <PasswordChangeModal
          isExpired={false}
          onClose={() => setShowPasswordModal(false)}
        />
      )}

      {/* Privacy & Data Rights */}
      {/* Integrazione API - solo utenti fornitore */}
      {user.ruolo === 100000005 && user.fornitore?.id && (
        <IntegrazioneApiProfilo fornitoreId={user.fornitore.id} />
      )}

      <Card title="Privacy e Dati Personali" className="mt-6">
        <div className="space-y-4">
          {/* Privacy acceptance info */}
          {user.privacyAccettata && (
            <div className="flex items-center justify-between py-3 border-b">
              <div>
                <p className="font-medium text-gray-900">Informativa Privacy</p>
                <p className="text-sm text-gray-500">
                  Accettata il{" "}
                  {user.dataPrivacyAccettata
                    ? new Date(user.dataPrivacyAccettata).toLocaleDateString(
                        "it-IT",
                      )
                    : "data non disponibile"}
                </p>
              </div>
              <a
                href="/api/privacy/informativa"
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-primary-600 hover:text-primary-700 font-medium"
              >
                Leggi informativa
              </a>
            </div>
          )}

          {/* View my data summary */}
          <div className="flex items-center justify-between py-3 border-b">
            <div>
              <p className="font-medium text-gray-900">I miei dati</p>
              <p className="text-sm text-gray-500">
                Visualizza un riepilogo dei dati personali trattati (Art. 15
                GDPR)
              </p>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={fetchPrivacySummary}
              loading={privacySummaryLoading}
            >
              Visualizza
            </Button>
          </div>

          {/* Summary display */}
          {privacySummary && (
            <div className="bg-gray-50 rounded-lg p-4 space-y-3">
              <div className="flex items-center justify-between">
                <h4 className="font-medium text-gray-900">
                  Riepilogo dati trattati
                </h4>
                <button
                  onClick={() => setPrivacySummary(null)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>

              {privacySummary.dataCategories?.length > 0 ? (
                <div className="space-y-2">
                  {privacySummary.dataCategories.map((cat, idx) => (
                    <div
                      key={idx}
                      className="flex items-center justify-between text-sm"
                    >
                      <div>
                        <span className="text-gray-700">{cat.category}</span>
                        <span className="text-gray-400 ml-2">
                          - {cat.description}
                        </span>
                      </div>
                      <Badge variant="default">{cat.count}</Badge>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-500">
                  Nessun dato personale trovato.
                </p>
              )}

              {privacySummary.processingPurposes && (
                <div className="mt-3 pt-3 border-t">
                  <p className="text-xs font-medium text-gray-500 mb-1">
                    Finalita del trattamento:
                  </p>
                  <p className="text-xs text-gray-500">
                    {privacySummary.processingPurposes.join(", ")}
                  </p>
                </div>
              )}

              {privacySummary.retentionPeriod && (
                <div className="pt-2">
                  <p className="text-xs font-medium text-gray-500 mb-1">
                    Periodo di conservazione:
                  </p>
                  <p className="text-xs text-gray-500">
                    {privacySummary.retentionPeriod}
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Export data */}
          <div className="flex items-center justify-between py-3 border-b">
            <div>
              <p className="font-medium text-gray-900">Esporta i miei dati</p>
              <p className="text-sm text-gray-500">
                Scarica una copia dei tuoi dati personali (Art. 20 GDPR -
                Portabilita)
              </p>
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleExportData("json")}
                loading={exportLoading === "json"}
                disabled={exportLoading !== null}
              >
                JSON
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleExportData("csv")}
                loading={exportLoading === "csv"}
                disabled={exportLoading !== null}
              >
                CSV
              </Button>
            </div>
          </div>

          {/* Request deletion */}
          <div className="flex items-center justify-between py-3">
            <div>
              <p className="font-medium text-gray-900">
                Richiedi cancellazione
              </p>
              <p className="text-sm text-gray-500">
                Richiedi la cancellazione dei tuoi dati personali (Art. 17 GDPR)
              </p>
            </div>
            <Button
              variant="outline"
              size="sm"
              className="text-red-600 border-red-300 hover:bg-red-50"
              onClick={() => setShowDeleteModal(true)}
            >
              Richiedi
            </Button>
          </div>
        </div>
      </Card>

      {/* Delete data modal */}
      <Modal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setDeleteReason("");
          setDeleteConfirmEmail("");
        }}
        title="Richiesta cancellazione dati"
        size="md"
        footer={
          <div className="flex justify-end gap-3">
            <Button
              variant="outline"
              onClick={() => {
                setShowDeleteModal(false);
                setDeleteReason("");
                setDeleteConfirmEmail("");
              }}
              disabled={deleteLoading}
            >
              Annulla
            </Button>
            <button
              onClick={handleDeleteRequest}
              disabled={deleteLoading || deleteConfirmEmail !== user?.email}
              className="px-4 py-2 text-sm font-medium rounded-lg bg-red-600 hover:bg-red-700 text-white disabled:opacity-50 flex items-center gap-2"
            >
              {deleteLoading && (
                <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                    fill="none"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                  />
                </svg>
              )}
              Conferma cancellazione
            </button>
          </div>
        }
      >
        <div className="space-y-4">
          <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-800 font-medium">Attenzione</p>
            <p className="text-sm text-red-700 mt-1">
              Questa azione disattivera il tuo account e inviera una richiesta
              di cancellazione al DPO. La cancellazione definitiva avverra entro
              30 giorni, previa verifica di eventuali obblighi di conservazione
              legale.
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Motivo della richiesta (opzionale)
            </label>
            <textarea
              value={deleteReason}
              onChange={(e) => setDeleteReason(e.target.value)}
              placeholder="Indica il motivo della richiesta..."
              className="form-input w-full h-20 resize-none"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Conferma inserendo la tua email: <strong>{user?.email}</strong>
            </label>
            <input
              type="email"
              value={deleteConfirmEmail}
              onChange={(e) => setDeleteConfirmEmail(e.target.value)}
              placeholder="Inserisci la tua email per confermare"
              className="form-input w-full"
            />
          </div>
        </div>
      </Modal>

      {/* Dev mode info */}
      {isDevMode && (
        <div className="mt-6 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <svg
              className="w-5 h-5 text-yellow-500 mt-0.5"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                clipRule="evenodd"
              />
            </svg>
            <div>
              <p className="text-sm text-yellow-800 font-medium">
                Modalità Sviluppo
              </p>
              <p className="text-sm text-yellow-700 mt-1">
                Stai usando un utente di test. In produzione, l'autenticazione
                sarà gestita da Azure AD B2C e il profilo sarà sincronizzato con
                Dataverse.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ProfilePage;
